import { isErrorWithData, isErrorWithErrors } from '@/services/helpers';
import {
  OrgV2PostPaymentPlanSetting,
  useGetOrganizationDetailsQuery,
  useGetOrganizationPaymentPlanQuery,
  useGetOrganizationPaymentSettingsQuery,
  usePutOrganizationPaymentSettingMutation,
  useUpdateOrganizationPaymentPlanMutation,
} from '@/services/organizations';
import { Box, Spinner, toast } from '@palmetto/palmetto-components';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ProgramType, ProgramTypeLabels } from 'types';

import PageHeader from '../../../PageHeader';

import { PaymentForm } from './PaymentForm';

export const EditPaymentSchedule = () => {
  const { alias, programType } = useParams<{ alias: any; programType: any }>();
  const { data: org, isLoading: isLoadingOrg } = useGetOrganizationDetailsQuery({ alias });
  const { data: paymentSettings, isLoading: isLoadingPaymentSettings } = useGetOrganizationPaymentSettingsQuery({
    alias,
  });
  const { data: orgV2PaymentPlanSettings, isLoading: isLoadingOrgV2PaymentPlanSettings } =
    useGetOrganizationPaymentPlanQuery({
      alias,
      programType,
    });
  const isLoading = isLoadingOrg || isLoadingPaymentSettings || isLoadingOrgV2PaymentPlanSettings;
  const currentPaymentPlanId = orgV2PaymentPlanSettings?.id ?? undefined;
  const initialValues = {
    shouldHavePaymentSchedule: paymentSettings?.inheritPaymentPlan ? 'no' : 'yes',
    paymentPlanId: '',
    legalEntity: paymentSettings?.inheritPaymentPlan ? 'no' : 'yes',
    setUpBankAccount: !paymentSettings || paymentSettings?.inheritBankAccount ? 'no' : 'yes',
    netSuiteVendorId: paymentSettings?.netSuiteVendorId ?? undefined,
    effectiveDate: undefined,
    planToApplyToExistingAccounts: undefined,
  };
  const [setPaymentSetting] = usePutOrganizationPaymentSettingMutation();
  const [setOrgV2PaymentPlanSetting] = useUpdateOrganizationPaymentPlanMutation();

  const navigate = useNavigate();
  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const savePayload = {
        alias,
        paymentSettings: {
          ...(paymentSettings?.payoutHold && { payoutHold: paymentSettings?.payoutHold }),
          netSuiteVendorId: values.netSuiteVendorId ? values.netSuiteVendorId : null,
          inheritBankAccount: values.setUpBankAccount === 'yes' ? false : true,
          inheritNetSuiteVendorId: values.legalEntity === 'yes' ? false : true,
          inheritPaymentPlan: values.paymentPlanId ? false : true,
        },
      };

      const result = await setPaymentSetting(savePayload);
      if ('error' in result) {
        toast.error('Error submitting settings');
        setSubmitting(false);
        return;
      }

      const payload: OrgV2PostPaymentPlanSetting = {
        alias,
        ...(values.shouldHavePaymentSchedule === 'yes' && { paymentPlanId: values.paymentPlanId }),
        inherit: values.shouldHavePaymentSchedule === 'yes' ? false : true,
        programType,
        ...(values.effectiveDate && { effectiveDate: new Date(values.effectiveDate).toISOString() }),
        ...(values.planToApplyToExistingAccounts && {
          planToApplyToExistingAccounts: values.planToApplyToExistingAccounts,
        }),
      };

      const paymentPlanRequest = await setOrgV2PaymentPlanSetting(payload);
      if ('error' in paymentPlanRequest) {
        const error = paymentPlanRequest.error;
        if (isErrorWithErrors(error) || isErrorWithData(error)) {
          const errorMessage = error.data.message;
          toast.error(errorMessage);
        } else {
          toast.error('Error submitting payment plan change');
        }
      } else {
        toast.success('Payment Plan submitted successfully');
        navigate(`/settings/organizations/${org?.alias}/payments`);
      }
    } catch (_e) {
      toast.error('Error submitting settings');
    }
    setSubmitting(false);
  };

  return (
    <>
      <Helmet>
        <title>Edit Organization</title>
      </Helmet>

      <Box
        childGap={{
          base: 'lg',
          desktop: '2xl',
          hd: '3xl',
        }}
        padding={{
          base: 'lg',
          desktop: '0',
        }}
        width="100%"
        style={{
          margin: '0 auto 0 0',
        }}
      >
        <PageHeader
          title={
            isLoading
              ? 'loading'
              : org?.name
                ? `${org.name} ${ProgramTypeLabels[programType as ProgramType]} Payment Plan`
                : 'organization'
          }
          eyebrow={
            <Link to={`/settings/organizations/${org?.alias}/payments`}>Organizations \ {org?.name} \ Payments</Link>
          }
        />
        {!isLoading && org ? (
          <PaymentForm
            currentPaymentPlanId={currentPaymentPlanId}
            initialValues={initialValues}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            organizationAlias={org.alias}
            programType={programType}
          />
        ) : (
          <Spinner size="lg" />
        )}
      </Box>
    </>
  );
};
